import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_RESPONSE_STATUS } from "utils/Constants";
import { showErrorModal } from "utils/hooks/handleNotification";
import { ClosingDataType, PropertiesType, WhiteLabelType } from "utils/Types";
import { common } from "utils/Wording";
import api from "../../ApiClient";
import { ApiURL } from "../../utils/Actions";
import moment from "moment";

const generateClosingDate = (openDate?: string, closeDate?: string) => {
  const isOpenDatePassed = openDate ? moment(new Date()).isAfter(openDate) : false;
  const isCloseDateNotPassed = closeDate ? moment(new Date()).isBefore(closeDate) : false;
  const isOpen = isOpenDatePassed && isCloseDateNotPassed;
  const closingDate = moment(closeDate).format("D MMM YYYY");
  return {
    isOpen,
    closingDate
  };
};

export type PropertiesSliceType = {
  data: PropertiesType;
  whiteLabel?: WhiteLabelType;
  showWelcomeModal?: boolean;
  closingData: ClosingDataType;
  loading: boolean;
};

const PropertiesInit: PropertiesSliceType = {
  data: {} as PropertiesType,
  whiteLabel: "",
  showWelcomeModal: false,
  closingData: {} as ClosingDataType,
  loading: true
};

export const fetchProperties = createAsyncThunk(ApiURL.config, async () => {
  try {
    const res = await api.get(ApiURL.config);
    if (res?.data?.status === API_RESPONSE_STATUS.ERROR) {
      throw new Error(common.loadingError);
    }
    return res?.data?.data ? res.data.data : [];
  } catch (error: any) {
    console.error(error);
    showErrorModal({
      title: common.failAsync,
      errors: error.response?.data?.errors ? error.response.data.errors : [error.message]
    });
    return PropertiesInit.data;
  }
});

export const fetchWhiteLabel = createAsyncThunk(ApiURL.whiteLabel, async () => {
  try {
    const res = await api.get(ApiURL.whiteLabel);
    if (res?.data?.status === API_RESPONSE_STATUS.ERROR) {
      throw new Error(common.loadingError);
    }
    return res?.data?.data ? res.data.data.name : "";
  } catch (error: any) {
    console.error(error);
    showErrorModal({
      title: common.failAsync,
      errors: error.response?.data?.errors ? error.response.data.errors : [error.message]
    });
    return PropertiesInit.whiteLabel;
  }
});

export const PropertiesSlice = createSlice({
  name: "properties",
  initialState: PropertiesInit,
  reducers: {
    setWelcomeModal: (state, action) => {
      state.showWelcomeModal = action.payload;
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchProperties.fulfilled, (state, action) => {
      const {
        ratingOpenDate,
        ratingCloseDate,
        "ratingOpenDate.os": ratingOpenDateOS,
        "ratingCloseDate.os": ratingCloseDateOS,
        easOpenDate,
        easCloseDate
      } = action.payload;
      const { isOpen: isSrsOpen, closingDate: srsClosingDate } = generateClosingDate(
        ratingOpenDate,
        ratingCloseDate
      );
      const { isOpen: isSrsOpenOS, closingDate: srsClosingDateOS } = generateClosingDate(
        ratingOpenDateOS,
        ratingCloseDateOS
      );
      const { isOpen: isEasOpen, closingDate: easClosingDate } = generateClosingDate(
        easOpenDate,
        easCloseDate
      );
      const closingObj = {
        isSrsOpen,
        srsClosingDate,
        isSrsOpenOS,
        srsClosingDateOS,
        isEasOpen,
        easClosingDate
      };

      state.closingData = closingObj;
      state.data = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchProperties.pending, () => {
      return PropertiesInit;
    });
    builder.addCase(fetchProperties.rejected, () => {
      return PropertiesInit;
    });
    builder.addCase(fetchWhiteLabel.fulfilled, (state, action) => {
      state.whiteLabel = action.payload;
    });
    builder.addCase(fetchWhiteLabel.rejected, () => {
      return PropertiesInit;
    });
  }
});

export default PropertiesSlice.reducer;

export const { setWelcomeModal } = PropertiesSlice.actions;
